import { DatePicker as AntdDatePicker } from 'antd';
import React, { ComponentProps, useMemo } from 'react';

import {
  dayjsToMoment,
  momentToDayjs,
  type DatetimeType,
} from '@common/services/datetime';

type AntdProps = ComponentProps<typeof AntdDatePicker>;

// NOTE: antd's PickerProps is a union, and a plain Omit over it keeps only the
// keys every member shares, dropping date-only props such as showToday.
type DistributiveOmit<T, K extends keyof never> = T extends unknown
  ? Omit<T, K>
  : never;

export type DatePickerProps = DistributiveOmit<
  AntdProps,
  'value' | 'defaultValue' | 'defaultPickerValue' | 'onChange' | 'disabledDate'
> & {
  // NOTE: keep this prop's identity stable across renders. A new object every
  // render re-bridges, and antd then discards whatever is being typed.
  value?: DatetimeType | null;
  defaultValue?: DatetimeType | null;
  defaultPickerValue?: DatetimeType | null;
  onChange?: (date: DatetimeType | null, dateString: string) => void;
  disabledDate?: (current: DatetimeType) => boolean;
};

export function DatePicker({
  value,
  defaultValue,
  defaultPickerValue,
  onChange,
  disabledDate,
  ...rest
}: DatePickerProps) {
  const momentValue = useMemo(() => dayjsToMoment(value), [value]);
  const momentDefaultValue = useMemo(
    () => dayjsToMoment(defaultValue) ?? undefined,
    [defaultValue]
  );
  const momentDefaultPickerValue = useMemo(
    () => dayjsToMoment(defaultPickerValue) ?? undefined,
    [defaultPickerValue]
  );

  return (
    <AntdDatePicker
      {...rest}
      value={momentValue}
      defaultValue={momentDefaultValue}
      defaultPickerValue={momentDefaultPickerValue}
      onChange={
        onChange
          ? (date, dateString) => onChange(momentToDayjs(date), dateString)
          : undefined
      }
      disabledDate={
        disabledDate
          ? (current) => disabledDate(momentToDayjs(current))
          : undefined
      }
    />
  );
}
